// ─── src/utils/track.js ──────────────────────────────────────────────────────
// LWD Intelligence Pipeline, Phase 1
//
// Every track() call persists to lwd_intelligence_events in Supabase.
// Fire-and-forget: synchronous from caller's perspective, async DB write.
// Works from day one, all existing call sites (search, shortlist, card
// clicks, etc.) are captured automatically with zero changes at call sites.
//
// Standard payload fields (extracted top-level, rest goes into metadata):
//   vendor_id , uuid of the listing / vendor being interacted with
//   category  , e.g. 'wedding-venues', 'wedding-planners'
//   country   , e.g. 'italy', 'uk'
//   region    , e.g. 'tuscany', 'london'
// ─────────────────────────────────────────────────────────────────────────────

import { supabase } from '../lib/supabaseClient';

const DEV = import.meta.env?.DEV ?? true;

// ── Anonymous session, persistent for the browser tab lifecycle ──────────────
function getSessionId() {
  const KEY = 'lwd_sid';
  try {
    let sid = sessionStorage.getItem(KEY);
    if (!sid) {
      sid = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `sid_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
      sessionStorage.setItem(KEY, sid);
    }
    return sid;
  } catch {
    return `sid_${Date.now()}`;
  }
}

// ── Device classification ─────────────────────────────────────────────────────
function getDeviceType() {
  const w = typeof window !== 'undefined' ? window.innerWidth : 1280;
  if (w < 768)  return 'mobile';
  if (w < 1024) return 'tablet';
  return 'desktop';
}

// ── Core track function ───────────────────────────────────────────────────────
/**
 * Fire a named intelligence event.
 * Synchronous from caller's perspective, DB write is fire-and-forget.
 *
 * @param {string} name     event identifier, e.g. "vendor_view"
 * @param {object} payload  optional context key/values
 */
export function track(name, payload = {}) {
  if (DEV) {
    // eslint-disable-next-line no-console
    console.log(`[LWD Intelligence] ${name}`, payload);
  }

  if (!supabase) return;

  // Hoist known top-level fields; everything else goes into metadata jsonb
  const { vendor_id, category, country, region, ...metadata } = payload;

  supabase
    .from('lwd_intelligence_events')
    .insert({
      event_type:  name,
      session_id:  getSessionId(),
      vendor_id:   vendor_id ?? null,
      category:    category  ?? null,
      country:     country   ?? null,
      region:      region    ?? null,
      page_path:   typeof window !== 'undefined' ? window.location.pathname : null,
      referrer:    typeof document !== 'undefined' ? (document.referrer || null) : null,
      device_type: getDeviceType(),
      metadata:    Object.keys(metadata).length ? metadata : {},
    })
    .then(({ error }) => {
      if (error && DEV) {
        // eslint-disable-next-line no-console
        console.warn('[LWD Intelligence] insert error:', error.message);
      }
    });
}

// Named alias, use either interchangeably
export { track as trackEvent };
